import {
  Fragment,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { UserContext } from 'globalContext/UserContext';
import moment from 'moment';
import { fetchOrderReferenceData, fetchOrders } from './api';
import DateField from './DateField';
import ExtendOrderButton from './ExtendOrderButton';
import OrderAccess from './OrderAccess';
import OrderAdminBanners from './OrderAdminBanners';
import OrderGroupTabs from './OrderGroupTabs';
import OrderRowDetails from './OrderRowDetails';
import OrderStatusCell from './OrderStatusCell';
import PageHeader from './PageHeader';
import RowExpandToggle from './RowExpandToggle';
import SearchableSelect from './SearchableSelect';
import {
  IssuedOrderState,
  OrderFilters,
  OrderSummary,
  SelectOption,
} from './types';
import useQueryState from './useQueryState';
import {
  DEFAULT_ORDER_GROUP,
  isInvalidDate,
  isOrderGroup,
  isOrderRenewable,
  ORDER_GROUP_STATUSES,
  ORDER_GROUPS,
  OrderGroup,
  RENEWAL_WINDOW_DAYS,
} from './utils';
import * as Styles from './Orders.css';

const EMPTY_PAGE = {
  content: [] as OrderSummary[],
  totalElements: 0,
};
const SEARCH_DELAY_MS = 400;
// Filters that narrow every tab; the Waiting count uses them too. Issued by
// is resolved separately because it has a default.
const BASE_FILTER_KEYS = [
  'q',
  'consultantId',
  'clientId',
  'dateFrom',
  'dateTo',
] as const;
const FILTER_KEYS = [
  'q',
  'consultantId',
  'clientId',
  'ownerId',
  'dateFrom',
  'dateTo',
  'deadlineWithinDays',
  'endingWithinDays',
  'withoutSuccessor',
  'sort',
] as const;

// One-click views inside the current tab; each sets its own URL parameters
// and clicking an active chip clears them again.
const QUICK_FILTERS: Array<{
  id: string;
  label: string;
  tooltip: string;
  patch: Record<string, string>;
}> = [
  {
    id: 'deadline',
    label: 'ORDERS.CHIP.DEADLINE_7',
    tooltip: 'ORDERS.CHIP.DEADLINE_7_HELP',
    patch: { deadlineWithinDays: '7' },
  },
  {
    id: 'ending',
    label: 'ORDERS.CHIP.ENDING_30',
    tooltip: 'ORDERS.CHIP.ENDING_30_HELP',
    patch: {
      endingWithinDays: String(RENEWAL_WINDOW_DAYS),
      withoutSuccessor: 'true',
    },
  },
];

const SORTABLE_COLUMNS = ['decisionDeadline', 'endDate'] as const;
type SortableColumn = typeof SORTABLE_COLUMNS[number];
// Client, Consultant and Issued by come first; the rest waits behind "More
// filters".
const MORE_FILTER_KEYS = ['q', 'dateFrom', 'dateTo'];
// Issued by: anyone. Without the parameter, an issuer sees their own
// assignments first, since the sales team splits clients between them, as in
// the other JIT apps (owner, 08.10).
export const ANY_OWNER = 'any';

interface SuccessMessage {
  id: string;
  orderNumber?: string;
}

const OrdersContent = ({ canManage }: { canManage: boolean }) => {
  const intl = useIntl();
  const { username } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation<IssuedOrderState | undefined>();
  const [issued] = useState(() =>
    location.state?.issuedOrderNumber ? location.state : undefined,
  );
  const [params, updateParams] = useQueryState();
  const appliedSearch = params.get('q') || '';
  const page = Number(params.get('page')) || 0;
  const size = Number(params.get('size')) || 20;
  const [search, setSearch] = useState(appliedSearch);
  const [moreFilters, setMoreFilters] = useState(() =>
    MORE_FILTER_KEYS.some((key) => params.get(key)),
  );
  const [orders, setOrders] = useState(EMPTY_PAGE);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [myAccountId, setMyAccountId] = useState<number | null>(null);
  const [referenceLoaded, setReferenceLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referencePartial, setReferencePartial] = useState(false);
  const [error, setError] = useState<'forbidden' | 'request' | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  // Rows expanded in place (change requests of 06.10 and 07.10); the detail
  // page stays for e-mail links.
  const [expanded, setExpanded] = useState<string[]>(() =>
    issued ? [issued.issuedOrderNumber] : [],
  );
  const [message, setMessage] = useState<SuccessMessage | null>(() =>
    issued
      ? {
          id: issued.extension
            ? 'ORDERS.SUCCESS.EXTEND'
            : 'ORDERS.SUCCESS.ISSUE',
          orderNumber: issued.issuedOrderNumber,
        }
      : null,
  );
  const [bannerVersion, setBannerVersion] = useState(0);
  const latestRequest = useRef(0);
  const tabParam = params.get('tab');
  const group: OrderGroup = isOrderGroup(tabParam)
    ? tabParam
    : DEFAULT_ORDER_GROUP;

  const numberParam = (key: string) => Number(params.get(key)) || undefined;
  const ownerParam = params.get('ownerId') || '';
  const defaultOwner = canManage && myAccountId ? String(myAccountId) : '';
  const ownerFilter =
    ownerParam === ANY_OWNER ? '' : ownerParam || defaultOwner;
  // An issuer's list waits for their account so it does not load everyone's
  // assignments first.
  const awaitingDefaultOwner = canManage && !ownerParam && !referenceLoaded;
  const ownerId = Number(ownerFilter) || undefined;
  const baseFilterKey = BASE_FILTER_KEYS.map((key) => params.get(key) || '')
    .concat(ownerFilter)
    .join('|');
  const filterKey = FILTER_KEYS.map((key) => params.get(key) || '')
    .concat(group, ownerFilter)
    .join('|');
  const baseFilters: OrderFilters = useMemo(
    () => ({
      search: appliedSearch || undefined,
      consultantId: numberParam('consultantId'),
      clientId: numberParam('clientId'),
      ownerId,
      dateFrom: params.get('dateFrom') || undefined,
      dateTo: params.get('dateTo') || undefined,
    }),
    // The key captures every filter value; params itself changes identity
    // on each navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseFilterKey],
  );
  const appliedFilters: OrderFilters = useMemo(
    () => ({
      search: appliedSearch || undefined,
      status: ORDER_GROUP_STATUSES[group],
      consultantId: numberParam('consultantId'),
      clientId: numberParam('clientId'),
      ownerId,
      dateFrom: params.get('dateFrom') || undefined,
      dateTo: params.get('dateTo') || undefined,
      deadlineWithinDays: numberParam('deadlineWithinDays'),
      endingWithinDays: numberParam('endingWithinDays'),
      withoutSuccessor: params.get('withoutSuccessor') === 'true' || undefined,
      sort: params.get('sort') || undefined,
    }),
    // The key captures every filter value; params itself changes identity
    // on each navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );

  // A quiet reload keeps the table, and so the expanded rows, mounted.
  const loadOrders = useCallback(
    async (quiet = false) => {
      const requestId = latestRequest.current + 1;
      latestRequest.current = requestId;
      if (!quiet) {
        setLoading(true);
      }
      setError(null);
      const result = await fetchOrders(appliedFilters, page, size);
      if (requestId !== latestRequest.current) {
        return;
      }
      if (result.ok && result.data) {
        setOrders({
          content: result.data.content,
          totalElements: result.data.totalElements,
        });
      } else {
        setOrders(EMPTY_PAGE);
        setError(result.status === 403 ? 'forbidden' : 'request');
      }
      setLoading(false);
    },
    [appliedFilters, page, size],
  );

  useEffect(() => {
    if (awaitingDefaultOwner) {
      return undefined;
    }
    loadOrders();
    return () => {
      latestRequest.current += 1;
    };
  }, [awaitingDefaultOwner, loadOrders]);

  // The Waiting badge counts pending orders under the same filters as the
  // list, like the badge in My work orders.
  const loadPendingCount = useCallback(async () => {
    const result = await fetchOrders(
      { ...baseFilters, status: ORDER_GROUP_STATUSES.pending },
      0,
      1,
    );
    setPendingCount(result.ok && result.data ? result.data.totalElements : 0);
  }, [baseFilters]);

  useEffect(() => {
    if (!awaitingDefaultOwner) {
      loadPendingCount();
    }
  }, [awaitingDefaultOwner, loadPendingCount]);

  // The message is shown once: the location state is dropped so a reload or
  // a later visit does not show it again.
  useEffect(() => {
    if (location.state) {
      history.replace({ pathname: location.pathname, search: location.search });
    }
  }, [history, location]);

  useEffect(() => {
    let mounted = true;
    fetchOrderReferenceData().then((result) => {
      if (!mounted) {
        return;
      }
      setEmployees(result.employees);
      setClients(result.clients);
      setMyAccountId(result.employeeIdsByUsername[username] ?? null);
      setReferencePartial(!result.ok);
      setReferenceLoaded(true);
    });
    return () => {
      mounted = false;
    };
  }, [username]);

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === appliedSearch) {
      return undefined;
    }
    const timer = setTimeout(
      () => updateParams({ q: trimmed, page: undefined }),
      SEARCH_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [appliedSearch, search, updateParams]);

  const setFilter = (key: string, value: string) =>
    updateParams({ [key]: value, page: undefined });

  const clearFilters = () => {
    setSearch('');
    updateParams(
      FILTER_KEYS.reduce<Record<string, undefined>>(
        (patch, key) => ({ ...patch, [key]: undefined }),
        { page: undefined },
      ),
    );
  };

  const searchFilter = (
    key: string,
    labelId: string,
    options: Array<{ value: string | number; label: string }>,
  ) => (
    <SearchableSelect
      id={`orders-filter-${key}`}
      label={intl.formatMessage({ id: labelId })}
      options={options}
      value={key === 'ownerId' ? ownerFilter : params.get(key) || ''}
      onChange={(value) =>
        setFilter(key, key === 'ownerId' ? value || ANY_OWNER : value)
      }
    />
  );

  const employeeOptions = employees.map(({ value, name }) => ({
    value,
    label: name,
  }));
  const clientOptions = clients.map(({ value, name }) => ({
    value,
    label: name,
  }));

  const dateFilter = (key: string, labelId: string) => (
    <DateField
      id={`orders-filter-${key}`}
      label={intl.formatMessage({ id: labelId })}
      value={params.get(key) || ''}
      onChange={(value) => !isInvalidDate(value) && setFilter(key, value)}
      size="small"
    />
  );

  const stop = (event: MouseEvent) => event.stopPropagation();

  const toggleRow = (orderNumber: string) =>
    setExpanded((current) =>
      current.includes(orderNumber)
        ? current.filter((number) => number !== orderNumber)
        : [...current, orderNumber],
    );

  // After End early or Cancel in an expanded row: confirm and refresh.
  const onRowChanged = (messageId: string) => {
    setMessage({ id: messageId });
    loadOrders(true);
    loadPendingCount();
    setBannerVersion((version) => version + 1);
  };

  const isQuickFilterActive = (patch: Record<string, string>) =>
    Object.entries(patch).every(([key, value]) => params.get(key) === value);

  const toggleQuickFilter = (patch: Record<string, string>) => {
    const active = isQuickFilterActive(patch);
    updateParams(
      Object.entries(patch).reduce<Record<string, string | undefined>>(
        (next, [key, value]) => ({
          ...next,
          [key]: active ? undefined : value,
        }),
        { page: undefined },
      ),
    );
  };

  const [sortColumn, sortDirection] = (params.get('sort') || '').split(',');
  const activeSort = SORTABLE_COLUMNS.includes(sortColumn as SortableColumn)
    ? (sortColumn as SortableColumn)
    : null;
  const toggleSort = (column: SortableColumn) => {
    let next: string | undefined = `${column},asc`;
    if (activeSort === column && sortDirection !== 'desc') {
      next = `${column},desc`;
    } else if (activeSort === column) {
      next = undefined;
    }
    updateParams({ sort: next, page: undefined });
  };
  const sortHeader = (column: SortableColumn, labelId: string) => (
    <TableSortLabel
      active={activeSort === column}
      direction={
        activeSort === column && sortDirection === 'desc' ? 'desc' : 'asc'
      }
      onClick={() => toggleSort(column)}
    >
      {intl.formatMessage({ id: labelId })}
    </TableSortLabel>
  );
  const endingChip = QUICK_FILTERS.find(({ id }) => id === 'ending')!;
  // The reminder lists its orders across all tabs.
  const showEndingOrders = () =>
    updateParams({ ...endingChip.patch, tab: 'all', page: undefined });

  return (
    <Styles.Page>
      <PageHeader
        title={intl.formatMessage({ id: 'ORDERS.TITLE' })}
        actions={
          canManage && (
            <>
              <Button component={Link} to="/assignments/bulk-extend">
                {intl.formatMessage({ id: 'ORDERS.BULK.RENEW_ACTION' })}
              </Button>
              <Button component={Link} to="/assignments/bulk" color="primary">
                {intl.formatMessage({ id: 'ORDERS.BULK.ISSUE_ACTION' })}
              </Button>
              <Button
                component={Link}
                to="/assignments/new"
                color="primary"
                variant="contained"
              >
                {intl.formatMessage({ id: 'ORDERS.CREATE' })}
              </Button>
            </>
          )
        }
      />

      {canManage && !awaitingDefaultOwner && (
        <OrderAdminBanners
          refreshKey={bannerVersion}
          endingListShown={isQuickFilterActive(endingChip.patch)}
          onShowEnding={showEndingOrders}
          ownerId={ownerId}
        />
      )}

      <Box display="flex" flexWrap="wrap" gridGap={8} mb={2}>
        {QUICK_FILTERS.map(({ id, label, tooltip, patch }) => {
          const active = isQuickFilterActive(patch);
          return (
            <Tooltip key={id} title={intl.formatMessage({ id: tooltip })}>
              <Chip
                label={intl.formatMessage({ id: label })}
                color={active ? 'primary' : 'default'}
                variant={active ? 'default' : 'outlined'}
                clickable
                aria-pressed={active}
                onClick={() => toggleQuickFilter(patch)}
                onDelete={active ? () => toggleQuickFilter(patch) : undefined}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Styles.Filters elevation={1}>
        {searchFilter('clientId', 'ORDERS.CLIENT', clientOptions)}
        {searchFilter('consultantId', 'ORDERS.CONSULTANT', employeeOptions)}
        {searchFilter('ownerId', 'ORDERS.OWNER', employeeOptions)}
        {moreFilters && (
          <>
            <TextField
              id="orders-filter-q"
              label={intl.formatMessage({ id: 'ORDERS.FILTER.SEARCH' })}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              variant="outlined"
              size="small"
            />
            {dateFilter('dateFrom', 'ORDERS.FILTER.DATE_FROM')}
            {dateFilter('dateTo', 'ORDERS.FILTER.DATE_TO')}
          </>
        )}
        <div>
          <Button onClick={() => setMoreFilters((open) => !open)}>
            {intl.formatMessage({
              id: moreFilters ? 'ORDERS.FILTER.FEWER' : 'ORDERS.FILTER.MORE',
            })}
          </Button>{' '}
          <Button onClick={clearFilters}>
            {intl.formatMessage({ id: 'ORDERS.FILTER.CLEAR' })}
          </Button>
        </div>
      </Styles.Filters>

      {error === 'forbidden' && (
        <Alert severity="warning">
          {intl.formatMessage({ id: 'ORDERS.NO_ACCESS' })}
        </Alert>
      )}
      {referencePartial && (
        <Alert severity="warning">
          {intl.formatMessage({ id: 'ORDERS.REFERENCE_PARTIAL' })}
        </Alert>
      )}
      {error === 'request' && (
        <Alert
          severity="error"
          action={
            <Button onClick={() => loadOrders()}>
              {intl.formatMessage({ id: 'ORDERS.RETRY' })}
            </Button>
          }
        >
          {intl.formatMessage({ id: 'ORDERS.ERROR' })}
        </Alert>
      )}

      {message && (
        <Box mb={2}>
          <Alert severity="success" onClose={() => setMessage(null)}>
            {intl.formatMessage(
              { id: message.id },
              {
                number: message.orderNumber && (
                  <Link to={`/assignments/${message.orderNumber}`}>
                    {message.orderNumber}
                  </Link>
                ),
              },
            )}
          </Alert>
        </Box>
      )}

      {!error && (
        <Paper>
          <OrderGroupTabs
            groups={ORDER_GROUPS}
            value={group}
            pendingCount={pendingCount}
            onChange={(value) => updateParams({ tab: value, page: undefined })}
          />
          {loading || awaitingDefaultOwner ? (
            <Styles.Page display="flex" justifyContent="center">
              <CircularProgress />
            </Styles.Page>
          ) : (
            <>
              <TableContainer>
                <Table
                  size="small"
                  aria-label={intl.formatMessage({ id: 'ORDERS.TITLE' })}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.NUMBER' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.CLIENT' })}
                      </TableCell>
                      <TableCell
                        sortDirection={
                          activeSort === 'endDate'
                            ? (sortDirection as 'asc' | 'desc') || 'asc'
                            : false
                        }
                      >
                        {sortHeader('endDate', 'ORDERS.PERIOD')}
                      </TableCell>
                      <TableCell
                        sortDirection={
                          activeSort === 'decisionDeadline'
                            ? (sortDirection as 'asc' | 'desc') || 'asc'
                            : false
                        }
                      >
                        {sortHeader('decisionDeadline', 'ORDERS.DEADLINE')}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.STATUS' })}
                      </TableCell>
                      <TableCell align="right">
                        {intl.formatMessage({ id: 'ORDERS.ACTIONS' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!orders.content.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {intl.formatMessage({ id: 'ORDERS.EMPTY' })}
                        </TableCell>
                      </TableRow>
                    )}
                    {orders.content.map((order) => {
                      const detailsUrl = `/assignments/${order.orderNumber}`;
                      const open = expanded.includes(order.orderNumber);
                      return (
                        <Fragment key={order.orderNumber}>
                          <Styles.ClickableRow
                            hover
                            selected={open}
                            onClick={() => toggleRow(order.orderNumber)}
                          >
                            <Styles.NoWrapCell>
                              <RowExpandToggle
                                open={open}
                                onToggle={() => toggleRow(order.orderNumber)}
                              />{' '}
                              <Link to={detailsUrl} onClick={stop}>
                                {order.orderNumber}
                              </Link>
                            </Styles.NoWrapCell>
                            <TableCell>
                              {order.consultantName}
                              {order.contractType && (
                                <Styles.CellNote>
                                  {intl.formatMessage({
                                    id: `ORDERS.CONTRACT.${order.contractType}`,
                                    defaultMessage: order.contractType,
                                  })}
                                </Styles.CellNote>
                              )}
                            </TableCell>
                            <TableCell>{order.clientName}</TableCell>
                            <Styles.NoWrapCell>
                              {moment(order.startDate).format('DD/MM/YYYY')} –{' '}
                              {moment(order.endDate).format('DD/MM/YYYY')}
                            </Styles.NoWrapCell>
                            <Styles.NoWrapCell>
                              {order.status === 'PENDING'
                                ? moment(order.decisionDeadline).format(
                                    'DD/MM/YYYY',
                                  )
                                : '—'}
                            </Styles.NoWrapCell>
                            <TableCell>
                              <OrderStatusCell order={order} />
                            </TableCell>
                            <Styles.NoWrapCell align="right" onClick={stop}>
                              {canManage && isOrderRenewable(order) && (
                                <ExtendOrderButton
                                  orderNumber={order.orderNumber}
                                />
                              )}
                            </Styles.NoWrapCell>
                          </Styles.ClickableRow>
                          {open && (
                            <TableRow>
                              <TableCell colSpan={7}>
                                <OrderRowDetails
                                  order={order}
                                  canManage={canManage}
                                  onChanged={onRowChanged}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={orders.totalElements}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_event, nextPage) =>
                  updateParams({ page: nextPage || undefined })
                }
                onRowsPerPageChange={(event) =>
                  updateParams({
                    page: undefined,
                    size: Number(event.target.value),
                  })
                }
              />
            </>
          )}
        </Paper>
      )}
    </Styles.Page>
  );
};

const Orders = () => (
  <OrderAccess>
    {({ canManage }) => <OrdersContent canManage={canManage} />}
  </OrderAccess>
);

export default Orders;
